import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, Search, CheckCircle2, Ribbon, Palmtree, Radio, Star, Heart, Users, Sparkles, ArrowUpDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageHead, Empty } from "@/components/common";
import { CategoryChips } from "@/components/CategoryChips";

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "followers", label: "Most followed" },
  { key: "rating", label: "Top rated" },
  { key: "newest", label: "Newest" },
];

function StoreCard({ s, onFollow, featured = false }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const follow = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.post(`/follow/${s.id}`);
      onFollow(s.id, r.following);
      toast.success(r.following ? "You'll be notified when they go live" : "Unfollowed");
    } catch (err) { toast.error(err.message || "Could not update"); }
    finally { setBusy(false); }
  };
  return (
    <div className="ss-card store-card" onClick={() => navigate(`/store/${s.id}`)} data-testid={`store-card-${s.id}`}
      style={{ padding: 18, cursor: "pointer", display: "flex", flexDirection: "column", gap: 12, height: featured ? "100%" : undefined, border: featured ? "1.5px solid var(--gold)" : undefined, boxShadow: featured ? "0 8px 24px rgba(198,160,40,.14)" : undefined }}>
      {featured && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, alignSelf: "flex-start", background: "var(--gold)", color: "var(--brand-2)", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999, letterSpacing: 0.5, textTransform: "uppercase" }} data-testid={`store-featured-${s.id}`}>
          <Sparkles size={11} /> Featured
        </div>
      )}
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          {s.avatar ? (
            <img src={s.avatar} alt="" style={{ width: 60, height: 60, borderRadius: 999, objectFit: "cover", border: "2.5px solid var(--gold)" }} />
          ) : (
            <div style={{ width: 60, height: 60, borderRadius: 999, border: "2.5px solid var(--gold)", background: "var(--brand)", color: "#fff", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 22 }} aria-hidden>
              {(s.name || "?").trim().charAt(0).toUpperCase()}
            </div>
          )}
          {s.is_live && (
            <span style={{ position: "absolute", bottom: -2, right: -2, background: "var(--red, #e23b3b)", color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 3, border: "2px solid var(--surface, #fff)" }} data-testid={`store-live-${s.id}`}>
              <Radio size={9} /> LIVE
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 16, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: featured ? 210 : 150 }}>{s.name}</span>
            {s.seller_verified && <CheckCircle2 size={15} className="badge-verified" />}
            {s.founding_seller && <span className="badge-founding" style={{ fontSize: 10 }}><Ribbon size={10} /> Founding{s.founding_number ? ` #${s.founding_number}` : ""}</span>}
          </div>
          {s.username && <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brand)" }}>@{s.username}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4, fontSize: 13, flexWrap: "wrap" }}>
            {s.seller_review_count > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }} data-testid={`store-rating-${s.id}`}>
                <Star size={13} fill="var(--gold)" color="var(--gold)" />
                <span style={{ fontWeight: 800 }}>{s.seller_rating.toFixed(1)}</span>
                <span className="ss-muted">· {s.seller_review_count}</span>
              </span>
            )}
            <span className="ss-muted" style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }} data-testid={`store-followers-${s.id}`}>
              <Users size={13} /> <b style={{ color: "var(--text, inherit)" }}>{s.follower_count}</b> follower{s.follower_count === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        {!s.is_self && (
          <button
            className={`ss-btn ss-btn-sm ${s.is_following ? "ss-btn-ghost" : "ss-btn-primary"}`}
            onClick={follow}
            disabled={busy}
            data-testid={`store-follow-${s.id}`}
            style={{ alignSelf: "flex-start", flexShrink: 0 }}
          >
            <Heart size={14} fill={s.is_following ? "currentColor" : "none"} /> {s.is_following ? "Following" : "Follow"}
          </button>
        )}
      </div>
      {s.bio && <div className="ss-muted" style={{ fontSize: 13, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.bio}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {s.categories.slice(0, 3).map((c) => (
          <span key={c} className="ss-pill" style={{ fontSize: 11, background: "var(--gold-soft)", color: "var(--brand)" }} data-testid={`store-cat-${s.id}-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c}</span>
        ))}
        {s.categories.length > 3 && <span className="ss-pill" style={{ fontSize: 11 }}>+{s.categories.length - 3}</span>}
        {s.on_vacation && <span className="ss-pill" style={{ fontSize: 11, background: "var(--gold)", color: "var(--brand-2)", display: "inline-flex", alignItems: "center", gap: 3 }}><Palmtree size={10} /> On vacation</span>}
      </div>
      <div className="ss-muted" style={{ fontSize: 12, marginTop: "auto", display: "flex", gap: 12 }}>
        {s.live_count > 0 && <span style={{ color: "var(--red, #e23b3b)", fontWeight: 700 }}>{s.live_count} live now</span>}
        {s.upcoming_count > 0 && <span>{s.upcoming_count} upcoming</span>}
        <span>{s.show_count} show{s.show_count === 1 ? "" : "s"}</span>
      </div>
    </div>
  );
}

export default function Stores() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("featured");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  useEffect(() => {
    api.get("/stores").then(setData).catch(() => setData({ categories: ["All"], stores: [] }));
  }, []);

  const onFollow = (id, following) => {
    setData((d) => !d ? d : {
      ...d,
      stores: d.stores.map((s) => s.id === id
        ? { ...s, is_following: following, follower_count: Math.max(0, s.follower_count + (following ? 1 : -1)) }
        : s),
    });
    refresh && refresh();
  };

  const sortFn = (a, b) => {
    if (sort === "followers") return b.follower_count - a.follower_count || b.seller_rating - a.seller_rating;
    if (sort === "rating") return b.seller_rating - a.seller_rating || b.seller_review_count - a.seller_review_count;
    if (sort === "newest") return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    // featured: live-first, then most-followed, then rating
    return (b.is_live - a.is_live) || (b.follower_count - a.follower_count) || (b.seller_rating - a.seller_rating);
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.stores.filter((s) => {
      const inCat = cat === "All" || (s.categories || []).includes(cat);
      const inQ = !term || (s.name || "").toLowerCase().includes(term) || (s.username || "").toLowerCase().includes(term) || (s.bio || "").toLowerCase().includes(term);
      const inVerified = !verifiedOnly || s.seller_verified;
      return inCat && inQ && inVerified;
    }).sort(sortFn);
  }, [data, cat, q, sort, verifiedOnly]);

  // Featured = top verified sellers (spotlight), shown only on the unfiltered default view.
  const featured = useMemo(() => {
    if (!data) return [];
    return data.stores
      .filter((s) => s.seller_verified && !s.is_self && (s.show_count > 0 || s.follower_count > 0))
      .sort((a, b) => (b.is_live - a.is_live) || (b.follower_count - a.follower_count) || (b.seller_rating - a.seller_rating))
      .slice(0, 6);
  }, [data]);

  if (data === null) return <Loading />;
  const showFeatured = featured.length > 0 && cat === "All" && !q.trim() && !verifiedOnly && sort === "featured";
  const featuredIds = new Set(showFeatured ? featured.map((s) => s.id) : []);
  const gridStores = filtered.filter((s) => !featuredIds.has(s.id));

  return (
    <div className="fade-up" data-testid="stores-page">
      <PageHead title="Browse Stores" sub="Discover Muslim-owned shops by category and follow your favourites — even when they're not live." />

      {showFeatured && (
        <div style={{ marginBottom: 22 }} data-testid="featured-stores">
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 17, marginBottom: 12 }}>
            <Sparkles size={18} style={{ color: "var(--gold)" }} /> Featured shops
            <span className="ss-muted" style={{ fontSize: 13, fontWeight: 600 }}>· top verified sellers</span>
          </div>
          <div className="featured-row" style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 6 }}>
            {featured.map((s) => (
              <div key={s.id} style={{ minWidth: 300, maxWidth: 320, flex: "0 0 auto" }}>
                <StoreCard s={s} onFollow={onFollow} featured />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <div className="ss-field" style={{ maxWidth: 360, flex: 1, minWidth: 220, height: 46 }}>
          <Search size={18} className="ss-muted" />
          <input placeholder="Search stores by name or @handle…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="stores-search" />
        </div>
        <div className="ss-field" style={{ height: 46, width: 190 }}>
          <ArrowUpDown size={16} className="ss-muted" />
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="stores-sort"
            style={{ border: "none", background: "transparent", outline: "none", flex: 1, fontSize: 14, cursor: "pointer" }}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <button
          className={`ss-btn ss-btn-sm ${verifiedOnly ? "ss-btn-primary" : "ss-btn-ghost"}`}
          onClick={() => setVerifiedOnly((v) => !v)}
          data-testid="stores-verified-toggle"
          style={{ height: 46 }}
        >
          <CheckCircle2 size={15} /> Verified only
        </button>
      </div>

      <div style={{ marginBottom: 18 }}>
        <CategoryChips categories={data.categories} selected={cat} onSelect={setCat} />
      </div>

      {gridStores.length === 0 ? (
        <Empty icon={Store} title={showFeatured ? "That's every shop for now" : "No stores found"} sub={showFeatured ? "More stores will appear here as sellers join." : (verifiedOnly ? "No verified stores match — turn off 'Verified only' or try another category." : (cat === "All" ? "Try a different search." : `No stores in ${cat} yet — try another category.`))} />
      ) : (
        <>
          <div className="ss-muted" style={{ fontSize: 13, marginBottom: 12 }}>{showFeatured ? "More shops" : `${gridStores.length} store${gridStores.length === 1 ? "" : "s"}${cat !== "All" ? ` in ${cat}` : ""}${verifiedOnly ? " · verified" : ""}`}</div>
          <div className="ss-grid grid-3" data-testid="stores-grid">
            {gridStores.map((s) => <StoreCard key={s.id} s={s} onFollow={onFollow} />)}
          </div>
        </>
      )}
    </div>
  );
}
